<template>
  <fragment>
    <div>
      <b-button v-b-toggle.sidebar-backdrop>
        Download Report
        <font-awesome-icon
          :icon="{
            prefix:'fas',
            iconName: 'download'
          }"
        />
      </b-button>
      <share-sidebar
        id="sidebar-backdrop"
        :generating="generating"
        @generate="generate()"
      />
    </div>
    <div v-if="generating" style="display: none">
      <div ref="content" class="root container">
        <div class="row">
          <div class="col-12 branding">
            <div class="college">
              <div
                v-if="collegeDetailsReport.data.logoUrl"
                class="avatar"
                :style="{
                  backgroundImage: `url(${collegeDetailsReport.data.logoUrl})`
                }"
              />

              <h1 class="name">{{ collegeDetailsReport.data.name }}</h1>
            </div>

            <img
              src="@/assets/img/bettermynd-logo-sm.png"
              alt="BetterMynd Logo"
            />
          </div>

          <div class="col-12">
            <p>
              <em>
                Document generated {{ new Date().toLocaleString() }}. Check your
                BetterMynd dashboard for current information.
              </em>
            </p>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <ul class="info-list">
              <li>
                <strong>{{ registeredStudents }}</strong>
                Registered {{ pluralize(registeredStudents, 'User') }}
              </li>
              <li>
                <strong>{{ scheduledSessions }}</strong>
                Scheduled {{ pluralize(scheduledSessions, 'Session') }} for
                <strong>{{ scheduledSessionStudents }}</strong>
                {{ pluralize(scheduledSessionStudents, 'User') }}
              </li>
              <li>
                <strong>{{ individualCreditsRemaining }}</strong>
                {{ pluralize(individualCreditsRemaining, 'Credit') }} Remaining
              </li>
              <li>
                <strong>{{ groupCreditsRemaining }}</strong>
                Wellbeing Workshop {{ pluralize(groupCreditsRemaining, 'Credit') }} Remaining
              </li>
              <li>
                <strong>{{ numberOfDays }}</strong> Estimated
                {{ pluralize(numberOfDays, 'Day') }} Remaining Until Credits
                Depleted
              </li>
            </ul>
          </div>
        </div>

        <div class="row usage">
          <div class="col-12">
            <h2>
              Credit Usage
              {{ from ? `from ${from}` : '' }}
              {{ through ? `through ${through}` : '' }}
            </h2>
            <template v-if="credits.customCampusTags.length">
              <span class="info-list">
                <b>Tags:</b>
                {{ credits.customCampusTags.reduce((prev, next) => !prev ? next : `${prev}, ${next}`) }}
              </span>
            </template>
          </div>
          <div class="col-6">
            <ul class="info-list">
              <li>
                <strong>{{ credits.counts.individual.adds }}</strong>
                {{ credits.counts.individual.adds === 1 ? "Credit" : "Credits" }}
                Added
              </li>
              <li>
                <strong>{{ individualCreditsUsed }}</strong> Individual Credits
                Used
                <ul>
                  <li>
                    <strong>{{ credits.counts.individual.successes + credits.counts.individual.upcomings }}</strong>
                    {{ credits.counts.individual.successes + credits.counts.individual.upcomings === 1 ? "credit" : "credits" }}
                    used on productive
                    {{ credits.counts.individual.successes + credits.counts.individual.upcomings === 1 ? "session" : "sessions" }}
                  </li>
                  <li>
                    <strong>{{ credits.counts.individual.lateCancels }}</strong>
                    {{ credits.counts.individual.lateCancels === 1 ? "credit" : "credits" }}
                    used on late canceled
                    {{ credits.counts.individual.lateCancels === 1 ? "session" : "sessions" }}
                  </li>
                  <li>
                    <strong>{{ credits.counts.individual.noShows }}</strong>
                    {{ credits.counts.individual.noShows === 1 ? "credit" : "credits" }}
                    used on not-attended
                    {{ credits.counts.individual.noShows === 1 ? "session" : "sessions" }}
                  </li>
                </ul>
              </li>
              <li>
                <strong>{{ individualAverageCreditsUsedPerStudent }}</strong>
                Average Per User
              </li>
              <li v-if="individualCreditsUsed > 0">
                <div class="distribution">
                  <strong>Distribution</strong>
                  <ul>
                    <li
                      v-for="item in individualCreditUseDistribution"
                      :key="item.creditsUsed"
                      class="item"
                    >
                      {{ item.students.toLocaleString() }} users used
                      <span class="credits">
                        {{ item.creditsUsed.toLocaleString() }}
                        {{ pluralize(item.creditsUsed, 'Credit') }}
                      </span>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </div>
          <div class="col-6">
            <ul class="info-list">
              <li>
                <strong>{{ credits.counts.workshop.adds }}</strong>
                {{ credits.counts.workshop.adds === 1 ? "Credit" : "Credits" }}
                Added
              </li>
              <li>
                <strong>{{ groupCreditsUsed }}</strong> Wellbeing Workshop Credits Used
              </li>
              <li>
                <strong>{{ groupAverageCreditsUsedPerStudent }}</strong> Average
                Per User
              </li>
              <li v-if="groupCreditsUsed > 0">
                <div class="distribution">
                  <strong>Distribution</strong>
                  <ul>
                    <li
                      v-for="item in groupCreditUseDistribution"
                      :key="item.creditsUsed"
                      class="item"
                    >
                      {{ item.students.toLocaleString() }} users used
                      <span class="credits">
                        {{ item.creditsUsed.toLocaleString() }}
                        {{ pluralize(item.creditsUsed, 'Credit') }}
                      </span>
                    </li>
                  </ul>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <h2 v-if="regFrom">
              Registrations from {{ regFrom }} through {{ regThrough }} (by
              {{ regBinBy }})
            </h2>
            <h2 v-else>
              All Registrations through {{ regThrough }} (by
              {{ regBinBy }})
            </h2>
            <template v-if="registrationsReport.parameters.customCampusTags.length">
              <span class="info-list">
                <b>Tags:</b>
                {{ registrationsReport.parameters.customCampusTags.reduce((prev, next) => !prev ? next : `${prev}, ${next}`) }}
              </span>
            </template>

            <p v-if="!binnedRegistrations.length" class="info-list">
              None
            </p>

            <ul class="print-data-list">
              <li v-for="(v, k) in registrationsChartData[0].data" :key="k">
                {{ k }}
                <strong>{{ v }}</strong>
              </li>
            </ul>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <h2>User Mental Health Concerns</h2>
            <template v-if="demographicsReport.customCampusTags.length">
              <span class="info-list">
                <b>Tags:</b>
                {{ demographicsReport.customCampusTags.reduce((prev, next) => !prev ? next : `${prev}, ${next}`) }}
              </span>
            </template>
            <p>
              This chart depicts the percentage of registered users who
              selected each concern. Users may identify multiple issues.
            </p>

            <ul class="print-data-list cells">
              <li v-for="(v, k) in specialty" :key="k">
                {{ k }} <strong>{{ v }}%</strong>
                <span class="bar-container">
                  <span
                    class="bar"
                    :style="{ width: (100 * v) / 100 + '%' }"
                  ></span>
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <h2>Registered User Demographics</h2>
            <template v-if="demographicsReport.customCampusTags.length">
              <span class="info-list">
                <b>Tags:</b>
                {{ demographicsReport.customCampusTags.reduce((prev, next) => !prev ? next : `${prev}, ${next}`) }}
              </span>
            </template>

            <h3>Gender</h3>
            <ul class="print-data-list cells">
              <li
                v-for="demographic in genderDemographics"
                :key="demographic.key + '_legend'"
              >
                {{ demographic.key }}
                <strong>
                  {{ Math.round((100 * demographic.value) / genderTotal) }}%
                </strong>
                <span class="bar-container">
                  <span
                    class="bar"
                    :style="{
                      width: (100 * demographic.value) / genderTotal + '%'
                    }"
                  ></span>
                </span>
              </li>
            </ul>

            <h3>Ethnicity</h3>
            <ul class="print-data-list cells">
              <li
                v-for="demographic in ethnicityDemographics"
                :key="demographic.key + '_legend'"
              >
                {{ demographic.key }}
                <strong>
                  {{ Math.round((100 * demographic.value) / genderTotal) }}%
                </strong>
                <span class="bar-container">
                  <span
                    class="bar"
                    :style="{
                      width: (100 * demographic.value) / genderTotal + '%'
                    }"
                  ></span>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </fragment>
</template>

<script>
import moment from 'moment';
import { sumBy } from '@/helpers';
import ShareSidebar from './ShareSidebar.vue';

const hiddenConcerns = ['Stress', 'None', 'Family Distress'];

const genderOptions = {
  male: 'Male',
  female: 'Female',
  transgender_female: 'Transgender-Female',
  transgender_male: 'Transgender-Male',
  non_binary: 'Non-Binary',
  prefer_not_to_say: 'Preferred not to answer',
  unknown: 'Exploring/Unknown',
  other: 'Not Listed / Self-Identify / Other',
  none: 'None',
  'n/a': 'N/A'
};

const ethnicityOptions = {
  white: 'White',
  black: 'Black or African American',
  hispanic: 'Hispanic',
  pacific: 'Pacific',
  asian: 'Asian',
  native: 'American Indian or Alaskan Native',
  multiracial: 'Multiracial',
  prefer_not_to_answer: 'Preferred not to answer',
  other: 'Other'
};

export default {
  name: 'ShareButton',

  components: {
    ShareSidebar
  },
  inject: [
    'collegeDetailsReport',
    'credits',
    'demographicsReport',
    'registrationsReport',
    'globalMetrics',
    'studentDetailsReport'
  ],

  data() {
    return {
      generating: false
    };
  },
  computed: {
    // header / primary data
    registeredStudents() {
      return this.studentDetailsReport.data.totalActiveStudents;
    },
    scheduledSessions() {
      return this.globalMetrics.data.requests.approved;
    },
    scheduledSessionStudents() {
      return this.globalMetrics.uniqueStudents;
    },
    individualCreditsRemaining() {
      return this.credits.forecast.individual.remaining;
    },
    groupCreditsRemaining() {
      return this.credits.forecast.workshop.remaining;
    },
    numberOfDays() {
      const estimate = this.credits.forecast.individual.estimatedCoverageDate;
      if (!estimate) { return 0; }
      const days = moment(estimate, 'YYYY-MM-DD').diff(moment(), 'day');

      if (days >= 180) {
        return '180+';
      }

      return days;
    },

    // credits usage
    from() {
      if (this.credits.from) { return moment(this.credits.from).format('LL'); }
      return null;
    },
    through() {
      if (this.credits.through) { return moment(this.credits.through).format('LL'); }
      return null;
    },
    binBy() {
      return this.credits.binBy;
    },
    individual() {
      return this.credits.usage.individual;
    },
    individualCreditsUsed() {
      return this.credits.counts.individual.successes
        + this.credits.counts.individual.upcomings
        + this.credits.counts.individual.lateCancels
        + this.credits.counts.individual.noShows;
    },
    individualAverageCreditsUsedPerStudent() {
      const average = this.credits.counts.individual.averagePerStudent;
      if (average) { return Number(average).toFixed(2); }
      return Number(0).toFixed(2);
    },
    individualCreditUseHistogram() {
      return this.credits.counts.individual.creditUseHistogram;
    },
    group() {
      return this.credits.usage.workshop;
    },
    groupCreditsUsed() {
      return this.credits.counts.workshop.successes
        + this.credits.counts.workshop.upcomings
        + this.credits.counts.workshop.lateCancels
        + this.credits.counts.workshop.noShows;
    },
    groupAverageCreditsUsedPerStudent() {
      const average = this.credits.counts.workshop.averagePerStudent;
      if (average) { return Number(average).toFixed(2); }
      return Number(0).toFixed(2);
    },
    groupCreditUseHistogram() {
      return this.credits.counts.workshop.creditUseHistogram;
    },

    // credits distribution
    individualCreditUseDistribution() {
      const chartData = [];

      for (let i = 0; i < this.individualCreditUseHistogram.length; i++) {
        const { students, creditsUsed } = this.individualCreditUseHistogram[i];

        chartData.push({
          students,
          creditsUsed
        });
      }

      return chartData;
    },
    groupCreditUseDistribution() {
      const chartData = [];

      for (let i = 0; i < this.groupCreditUseHistogram.length; i++) {
        const { students, creditsUsed } = this.groupCreditUseHistogram[i];

        chartData.push({
          students,
          creditsUsed
        });
      }

      return chartData;
    },

    // registrations
    regFrom() {
      return this.registrationsReport.parameters.from ? moment(this.registrationsReport.parameters.from).format('LL') : null;
    },
    regThrough() {
      return moment(this.registrationsReport.parameters.through).format('LL');
    },
    regBinBy() {
      return this.registrationsReport.parameters.binBy;
    },
    binnedRegistrations() {
      return this.registrationsReport.data.binnedRegistrations;
    },
    registrationsChartData() {
      return [
        {
          name: 'Registrations',
          data: this.binnedRegistrations.reduce(
            (a, e) => ({ ...a, [e.date]: e.count }),
            {}
          )
        }
      ];
    },

    // concerns
    concerns() {
      return this.demographicsReport.data.concerns;
    },
    specialty() {
      if (!this.concerns) {
        return {};
      }

      return this.concerns
        .filter((e) => hiddenConcerns.indexOf(e.answer_text) < 0)
        .reduce((a, e) => {
          a[e.answer_text] = Math.round(
            (e.number_of_responses / this.demographicsReport.data.totalActiveStudents) * 100
          );
          return a;
        }, {});
    },

    // demographics
    demographicData() {
      return this.demographicsReport.data.demographics;
    },
    demographics() {
      return Object.entries(this.demographicData)
        .map(([key, value]) => ({ key, value }))
        .filter((x) => x.value > 0);
    },
    genderDemographics() {
      return this.demographics
        .filter((x) => x.key.startsWith('gender_'))
        .map(({ key, value }) => ({ key: key.replace('gender_', ''), value }))
        .map(({ key, value }) => ({ key: genderOptions[key], value }));
    },
    genderTotal() {
      return this.genderDemographics.reduce(sumBy('value'), 0);
    },
    ethnicityDemographics() {
      return this.demographics
        .filter((x) => x.key.startsWith('race_'))
        .map(({ key, value }) => ({ key: key.replace('race_', ''), value }))
        .map(({ key, value }) => ({ key: ethnicityOptions[key], value }));
    },
    ethnicityTotal() {
      return this.ethnicityDemographics.reduce(sumBy('value'), 0);
    }
  },

  methods: {
    async generate() {
      this.generating = true;
      await this.$nextTick();

      const pdf = await window
        .html2pdf()
        .set({
          // Breaks based on top level elements inside ref=content (div.row)
          pagebreak: { mode: 'avoid-all' },
          filename: `${this.collegeDetailsReport.data.name} BetterMynd Usage.pdf`
        })
        .from(this.$refs.content)
        .toPdf()
        .get('pdf');
      this.generating = false;

      window.open(pdf.output('bloburl'), '_blank');
    },

    pluralize(num, string) {
      if (num === 1) {
        return string;
      }

      return `${string}s`;
    }
  }
};
</script>

<style lang="scss" scoped>
.root {
  color: #000;
  font-family: $sans;
}

.container {
  padding: 15px 50px 0 50px;
}

.branding {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;

  .college {
    display: flex;
    align-items: center;
    margin: 1.25rem 0;
  }

  img {
    max-height: 48px;
    width: auto;
  }
}

p {
  font-size: 13px;
}

h1,
h2,
h3 {
  color: #000;
  font-family: $sans;
  font-size: 18px;
  font-weight: bold;
  line-height: 28px;
  margin: 0;
  padding: 0;
  letter-spacing: -0.03rem;
}

h2 {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 8px;
  margin-top: 8px;
}

h3 {
  font-size: 12px;
  margin: 0;
}

.avatar {
  @include background-cover;
  @include border-radius(50%);

  display: inline-block;
  background-color: $dark-blue;
  height: 32px;
  min-width: 32px;
  width: 32px;
  margin-right: 8px;

  &.default {
    background-image: url('~@/assets/img/default-icon.jpg');
  }
}

.info-list {
  list-style: none;
  padding-left: 0;
  font-size: 12px;
  line-height: 1.8;
}

.print-data-list {
  width: auto;
  display: inline-block;
  max-width: 648px;

  &.cells li {
    width: 152px;
  }
}

.distribution {
  margin: 8px 0;
}
</style>
